/**
 * WidgetStatus — per-widget overlay for dashboard cards driven by
 * useSerialQueries. Renders a small inline spinner while loading, a
 * retry affordance on error/timeout, and is invisible on success.
 *
 * Usage:
 *   <div className="relative">
 *     <StatCard value={results.stats?.total ?? '—'} />
 *     <WidgetStatus status={statuses.stats} error={errors.stats}
 *                   onRetry={() => retry('stats')} />
 *   </div>
 */
import React from 'react';
import { Loader2, AlertTriangle, Clock } from 'lucide-react';
import type { QueryStatus } from '../hooks/useSerialQueries';

export interface WidgetStatusProps {
  status: QueryStatus | undefined;
  error?: string;
  onRetry?: () => void;
  /** Label shown next to the error/timeout message. Optional. */
  label?: string;
}

export const WidgetStatus: React.FC<WidgetStatusProps> = ({ status, error, onRetry, label }) => {
  if (!status || status === 'idle' || status === 'ok') return null;

  if (status === 'loading') {
    return (
      <div className="absolute top-2 right-2 pointer-events-none" aria-label="Loading">
        <Loader2 className="w-4 h-4 text-slate-400 animate-spin" />
      </div>
    );
  }

  // error | timeout
  const isTimeout = status === 'timeout';
  const Icon = isTimeout ? Clock : AlertTriangle;
  const color = isTimeout ? 'text-amber-500' : 'text-rose-500';
  const msg = isTimeout ? 'Timed out' : 'Failed';

  return (
    <div
      className="absolute inset-x-0 bottom-0 flex items-center justify-between gap-2 px-2 py-1 text-[11px] bg-slate-900/70 text-slate-200 rounded-b"
      role="alert"
    >
      <span className={`flex items-center gap-1 ${color}`}>
        <Icon className="w-3 h-3" />
        <span>{label ? `${label}: ${msg}` : msg}</span>
      </span>
      {onRetry && (
        <button
          type="button"
          onClick={onRetry}
          className="text-slate-300 hover:text-white underline underline-offset-2"
        >
          Retry
        </button>
      )}
    </div>
  );
};

export default WidgetStatus;
